/**
 * Daten der Kosten-Seite eines Antrags.
 *
 * Gelesen wird über den projektweiten Endpunkt und auf den Antrag gefiltert, geschrieben über
 * die antragsbezogenen Pfade – die Antrags-ID steht in jeder Zeile bzw. wird beim Anlegen
 * mitgegeben.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { permitsApi } from "../../../../services/api/permits";
import { parseApiError } from "../../../../services/api/errorPresentation";
import { hasAnyFieldChanged } from "../../../../utils/formUtils";
import { useSaveState } from "../../../../hooks/useSaveState";
import {
  PermitCostItemCreate,
  PermitCostItemUpdate,
  PermitCostSummary,
  PermitProcedure,
  ProjectPermitCostItem,
} from "../../../../types";

interface UseProjectPermitCostsProps {
  projectId: string | undefined;
  /** Der Antrag, dessen Kosten die Seite zeigt */
  procedureId: string;
}

export function useProjectPermitCosts({
  projectId,
  procedureId,
}: UseProjectPermitCostsProps) {
  const [costItems, setCostItems] = useState<ProjectPermitCostItem[]>([]);
  const [procedures, setProcedures] = useState<PermitProcedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Speicher-Zustand aller Schreibvorgänge dieser Seite; PageTemplate macht daraus den Toast.
  const { isSaving, saveError, speichern } = useSaveState();

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [costItemsData, proceduresData] = await Promise.all([
        permitsApi.getProjectCostItems(projectId),
        permitsApi.getPermitProcedures(projectId),
      ]);
      setCostItems(costItemsData ?? []);
      setProcedures(proceduresData ?? []);
    } catch (err) {
      setError(parseApiError(err).message);
      setCostItems([]);
      setProcedures([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const createCostItem = useCallback(
    async (
      permitProcedureId: string,
      data: PermitCostItemCreate,
    ): Promise<void> => {
      if (!projectId) throw new Error("Keine Projekt-ID");
      const erstellt = await permitsApi.createCostItem(
        projectId,
        permitProcedureId,
        data,
      );
      const antrag = procedures.find(
        (procedure) => procedure.id === permitProcedureId,
      );
      setCostItems((prev) => [
        ...prev,
        {
          ...erstellt,
          permit_procedure_bezeichnung:
            antrag?.bezeichnung || antrag?.procedure_type.name || "",
        },
      ]);
    },
    [projectId, procedures],
  );

  /**
   * Speichert ein Feld einer Kostenposition (Blur-Pattern in der Tabelle) – mit Dirty-Check,
   * sonst meldete jedes Durchtabben einen Speichervorgang.
   */
  const updateCostItem = useCallback(
    async (costItemId: string, data: PermitCostItemUpdate): Promise<void> => {
      const vorhanden = costItems.find((item) => item.id === costItemId);
      if (!projectId || !vorhanden) return;
      if (!hasAnyFieldChanged(vorhanden, data)) return;

      await speichern(async () => {
        const aktualisiert = await permitsApi.updateCostItem(
          projectId,
          vorhanden.permit_procedure_id,
          costItemId,
          data,
        );
        setCostItems((prev) =>
          prev.map((item) =>
            item.id === costItemId ? { ...item, ...aktualisiert } : item,
          ),
        );
      });
    },
    [projectId, costItems, speichern],
  );

  const deleteCostItem = useCallback(
    async (costItemId: string): Promise<void> => {
      const vorhanden = costItems.find((item) => item.id === costItemId);
      if (!projectId || !vorhanden)
        throw new Error("Kostenposition nicht gefunden");
      await permitsApi.deleteCostItem(
        projectId,
        vorhanden.permit_procedure_id,
        costItemId,
      );
      setCostItems((prev) => prev.filter((item) => item.id !== costItemId));
    },
    [projectId, costItems],
  );

  const kostenDesAntrags = useMemo(
    () => costItems.filter((item) => item.permit_procedure_id === procedureId),
    [costItems, procedureId],
  );
  const antrag = useMemo(
    () => procedures.find((procedure) => procedure.id === procedureId) ?? null,
    [procedures, procedureId],
  );

  /**
   * Erfasste Plankosten und bereits beauftragte Kosten des Antrags. Über alle Anträge ergeben
   * sie den abgeleiteten CAPEX-Abschnitt "Genehmigung" in der Kalkulation.
   *
   * Bewusst im Frontend gerechnet: die Summe folgt so unmittelbar jeder Änderung.
   */
  const costSummary = useMemo<PermitCostSummary>(
    () => ({
      summe_geplant: kostenDesAntrags.reduce(
        (summe, item) => summe + (item.betrag_geplant ?? 0),
        0,
      ),
      summe_ist: kostenDesAntrags.reduce(
        (summe, item) => summe + (item.betrag_ist ?? 0),
        0,
      ),
      anzahl_positionen: kostenDesAntrags.length,
    }),
    [kostenDesAntrags],
  );

  return {
    costItems: kostenDesAntrags,
    /** Der Antrag der Seite; null, wenn es ihn (nicht mehr) gibt */
    antrag,
    costSummary,
    loading,
    error,
    isSaving,
    saveError,
    reload: load,
    createCostItem,
    updateCostItem,
    deleteCostItem,
  };
}
